import React, { useState, useEffect } from 'react';

/**
 * Varsaphala (Solar Return) Display Component
 * Shows annual predictions based on solar return chart
 * Now with year selector to view any year
 */
const VarsaphalaDisplay = ({ data, natalData }) => {
  const [expandedSection, setExpandedSection] = useState('overview');
  const [selectedYear, setSelectedYear] = useState(data?.target_year || new Date().getFullYear());
  const [yearData, setYearData] = useState(data);
  const [loading, setLoading] = useState(false);

  // Generate year options
  const birthYear = natalData?.birth_date ? parseInt(natalData.birth_date.split('-')[0]) : 1997;
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = birthYear; y <= currentYear + 50; y++) {
    yearOptions.push(y);
  }

  // Load data for selected year
  useEffect(() => {
    if (selectedYear !== data?.target_year && natalData) {
      loadYearData();
    }
  }, [selectedYear]);

  const loadYearData = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${BACKEND_URL}/api/varsaphala/single-year`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: natalData.person_id || 'temp',
          natal_sun_lon: natalData.sun_lon,
          natal_asc_sign_idx: natalData.asc_sign_idx,
          natal_date: natalData.birth_date,
          target_year: selectedYear,
          lat: natalData.latitude,
          lon: natalData.longitude
        })
      });
      if (response.ok) {
        const result = await response.json();
        setYearData(result.varsaphala);
      }
    } catch (error) {
      console.error('Error loading year data:', error);
    } finally {
      setLoading(false);
    }
  };

  const displayData = yearData || data;

  if (!displayData || Object.keys(displayData).length === 0) {
    return <div className="p-4 bg-gray-50 rounded">No Varsaphala data available</div>;
  }

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Extract nested varsha_lagna data
  const varshaLagnaData = displayData.varsha_lagna?.varsha_lagna || displayData.varsha_lagna || {};
  const solarReturnDate = displayData.varsha_lagna?.solar_return_datetime || 'N/A';
  const varshaLagnaSign = varshaLagnaData.sign || 'N/A';
  const varshaLagnaDegree = varshaLagnaData.degree_in_sign || 0;
  const ageCompleted = selectedYear - birthYear;

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 rounded-xl text-white">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-3xl">☀️</span>
              <h2 className="text-2xl font-bold">Varsaphala - Year by Year</h2>
            </div>
            <p className="text-amber-100 mt-2">Annual predictions based on solar return chart</p>
          </div>
          
          {/* Year Selector */}
          {natalData && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedYear(Math.max(birthYear, selectedYear - 1))}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all disabled:opacity-50"
                disabled={selectedYear <= birthYear || loading}
              >
                ← Prev
              </button>
              
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 bg-white text-gray-800 rounded-lg font-semibold text-lg cursor-pointer"
                disabled={loading}
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>
                    {year} (Age {year - birthYear})
                  </option>
                ))}
              </select>
              
              <button
                onClick={() => setSelectedYear(Math.min(currentYear + 50, selectedYear + 1))}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all disabled:opacity-50"
                disabled={selectedYear >= currentYear + 50 || loading}
              >
                Next →
              </button>
            </div>
          )}
        </div>
        {loading && <div className="text-amber-100 text-sm mt-2">Loading year {selectedYear}...</div>}
      </div>

      {/* Overview Card - Parashara Light Style */}
      <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-amber-200">
        <div className="text-center mb-4">
          <div className="text-sm text-gray-500 mb-2">Completed</div>
          <div className="text-5xl font-bold text-amber-600 mb-2">
            {ageCompleted} Year{ageCompleted !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t-2 border-amber-100 pt-4">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Target Year</div>
            <div className="text-2xl font-bold text-amber-600">{selectedYear}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Solar Return Date & Time</div>
            <div className="text-sm font-semibold text-gray-700">
              {new Date(solarReturnDate).toLocaleDateString('en-IN', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
              <br />
              {new Date(solarReturnDate).toLocaleTimeString('en-IN', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false
              })} hrs
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Birth to Solar Return</div>
            <div className="text-xl font-bold text-orange-600">{ageCompleted} years</div>
          </div>
        </div>
      </div>

      {/* Main Chart Info - Parashara Light Layout */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h3 className="text-xl font-bold mb-4 text-gray-800">📊 Varsaphala Chart Details</h3>
        
        {/* Key Indicators Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Varsha Lagna (As) */}
          <div className="text-center p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200">
            <div className="text-xs text-gray-600 mb-1">As (Varsha Lagna)</div>
            <div className="text-lg font-bold text-indigo-700">{varshaLagnaSign}</div>
            <div className="text-sm text-gray-600">{varshaLagnaDegree.toFixed(2)}°</div>
          </div>

          {/* Year Lord (YL) */}
          <div className="text-center p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
            <div className="text-xs text-gray-600 mb-1">YL (Year Lord)</div>
            <div className="text-lg font-bold text-purple-700">{displayData.varshesa?.varshesa || displayData.varshesa?.year_lord || 'N/A'}</div>
          </div>

          {/* Muntha (Mun) */}
          <div className="text-center p-4 bg-pink-50 rounded-lg border-2 border-pink-200">
            <div className="text-xs text-gray-600 mb-1">Mun (Muntha)</div>
            <div className="text-lg font-bold text-pink-700">{displayData.muntha?.muntha_sign || displayData.muntha?.sign || 'N/A'}</div>
            <div className="text-xs text-gray-500">House {displayData.muntha?.muntha_house_from_lagna || displayData.muntha?.house_from_lagna || 'N/A'}</div>
          </div>

          {/* Munthesha */}
          <div className="text-center p-4 bg-rose-50 rounded-lg border-2 border-rose-200">
            <div className="text-xs text-gray-600 mb-1">Munthesha</div>
            <div className="text-lg font-bold text-rose-700">{displayData.muntha?.munthesha || 'N/A'}</div>
          </div>
        </div>

        {/* Planetary Positions at Solar Return */}
        {displayData.varsha_lagna?.varsha_planets && Object.keys(displayData.varsha_lagna.varsha_planets).length > 0 && (
          <div className="border-t-2 border-gray-200 pt-6">
            <h4 className="text-lg font-bold text-gray-800 mb-4">Planetary Positions at Solar Return</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Object.entries(displayData.varsha_lagna.varsha_planets).map(([planet, data]) => {
                const planetColors = {
                  'Sun': 'bg-orange-100 text-orange-700 border-orange-300',
                  'Moon': 'bg-blue-100 text-blue-700 border-blue-300',
                  'Mars': 'bg-red-100 text-red-700 border-red-300',
                  'Mercury': 'bg-green-100 text-green-700 border-green-300',
                  'Jupiter': 'bg-yellow-100 text-yellow-700 border-yellow-300',
                  'Venus': 'bg-pink-100 text-pink-700 border-pink-300',
                  'Saturn': 'bg-gray-100 text-gray-700 border-gray-300'
                };
                return (
                  <div
                    key={planet}
                    className={`p-4 rounded-lg border-2 ${planetColors[planet] || 'bg-gray-50 text-gray-700 border-gray-200'}`}
                  >
                    <div className="font-bold text-sm mb-2">{planet}</div>
                    <div className="space-y-1 text-sm">
                      <div>
                        <span className="font-semibold">{data.sign}</span>
                        {data.retrograde && <span className="ml-1 text-red-600">(R)</span>}
                      </div>
                      <div className="text-xs">
                        {data.longitude?.toFixed(2)}°
                      </div>
                      <div className="text-xs text-gray-600">
                        {(data.longitude % 30)?.toFixed(2)}° in sign
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sections removed - now integrated into main chart above */}

      {/* Sahamas (Arabic Parts) */}
      {data.sahamas && Object.keys(data.sahamas).length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <button
            onClick={() => toggleSection('sahamas')}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-bold text-gray-800">🌟 Sahamas (Arabic Parts)</h3>
            <span className="text-2xl">{expandedSection === 'sahamas' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'sahamas' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(data.sahamas).map(([name, sahamaData]) => (
                <div key={name} className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                  <div className="font-semibold text-purple-700 mb-2 capitalize">
                    {name.replace('_', ' ')}
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Sign: </span>
                    <span className="font-medium">{sahamaData.sign}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Longitude: </span>
                    <span className="font-medium">{sahamaData.longitude?.toFixed(2)}°</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mudda Dasha (Monthly Periods) */}
      {data.mudda_dasha && data.mudda_dasha.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <button
            onClick={() => toggleSection('mudda')}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-bold text-gray-800">📅 Mudda Dasha (Monthly Periods)</h3>
            <span className="text-2xl">{expandedSection === 'mudda' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'mudda' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                  <tr>
                    <th className="p-3 text-left">Month</th>
                    <th className="p-3 text-left">Planet</th>
                    <th className="p-3 text-left">Start Date</th>
                    <th className="p-3 text-left">End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mudda_dasha.map((period, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3">{period.month}</td>
                      <td className="p-3 font-semibold text-blue-600">{period.planet}</td>
                      <td className="p-3 text-sm">{period.start_date}</td>
                      <td className="p-3 text-sm">{period.end_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Year Themes */}
      {data.year_themes && data.year_themes.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h3 className="text-xl font-bold mb-4 text-gray-800">💡 Year Themes & Guidance</h3>
          <div className="space-y-3">
            {data.year_themes.map((theme, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-4 border-cyan-500">
                <div className="font-semibold text-cyan-700 mb-1">{theme.area || theme.theme}</div>
                <div className="text-sm text-gray-700">{theme.description || theme.interpretation}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tajika Yogas */}
      {data.tajika_yogas && data.tajika_yogas.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <button
            onClick={() => toggleSection('yogas')}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-bold text-gray-800">🔮 Tajika Yogas</h3>
            <span className="text-2xl">{expandedSection === 'yogas' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'yogas' && (
            <div className="space-y-3">
              {data.tajika_yogas.map((yoga, idx) => (
                <div key={idx} className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                  <div className="font-semibold text-purple-700">{yoga.name}</div>
                  <div className="text-sm text-gray-700 mt-1">{yoga.description}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VarsaphalaDisplay;
